import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails, Chip, Stack, CircularProgress,
  Paper, IconButton, Tooltip, Snackbar, Alert, TextField, MenuItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ControlsGrid, { NOT_APPLICABLE_COLOR, buildDecisionLookup, controlOutcome, getControlTitle } from './ControlsGrid';
import { useDispatch, useSelector } from 'react-redux';
import { downloadComplianceReport, clearAllComplianceMessages } from '../../../store/compliance/complianceSlice';
import { getComplianceControls } from '../../../helpers/auth_aws_helper';
import { AH_BORDER, AH_MUTED, AH_TEXT, AH_TRACK, searchFieldSx, tableShellSx } from '../AuditHub/theme';

const DEFAULT_METRICS = [
  { key: 'total', label: 'Total Controls', color: '#495057' },
  { key: 'passed', label: 'Passed', color: '#2e7d32' },
  { key: 'failed', label: 'Failed', color: '#d32f2f' },
  { key: 'manual', label: 'Manual', color: '#1976d2' },
];

const AUDIT_METRICS = [
  { ...DEFAULT_METRICS[0], color: AH_TEXT },
  ...DEFAULT_METRICS.slice(1, 3),
  { key: 'notApplicable', label: 'Not Applicable', color: NOT_APPLICABLE_COLOR },
];

const METRIC_SEPARATOR = '#9ca3af';

const SECTION_CONCURRENCY = 4;

/** Only what the section counts need, so every section's full control payload is not kept in memory. */
const summarizeRows = (list, compliance) => {
  const rows = Array.isArray(list) ? list : list && typeof list === 'object' ? Object.values(list) : null;
  return rows
    ? rows.map((row) => ({ ControlId: row.ControlId, Status: row.Status, title: getControlTitle(row, compliance) }))
    : null;
};

const SECTION_FAILED = 'failed';

const textKey = (value) => String(value ?? '').trim().toLowerCase();

/** Assignee email for a control, matched the same way review status is matched. */
const assigneeLookup = (rosterControls) => {
  const byCode = new Map();
  const byTitle = new Map();
  (Array.isArray(rosterControls) ? rosterControls : []).forEach((item) => {
    const email = String(item?.assigneeEmail || '').trim();
    if (!email) return;
    const code = textKey(item.controlCode);
    const title = textKey(item.controlTitle);
    if (code && !byCode.has(code)) byCode.set(code, email);
    if (title && !byTitle.has(title)) byTitle.set(title, email);
  });
  return (controlId, title) => {
    const code = textKey(controlId);
    if (code && byCode.has(code)) return byCode.get(code);
    const name = textKey(title);
    return (name && byTitle.get(name)) || '';
  };
};

const sameEmail = (left, right) => textKey(left) === textKey(right);

const loadSectionRows = async (compliance, runId, section) => {
  const res = await getComplianceControls({ compliance, runId, section }).catch(() => null);
  return (res?.status === 200 && summarizeRows(res.controlsListJson, compliance)) || SECTION_FAILED;
};

/** Controls of every section, loaded once per run so collapsed sections can be counted too. */
const useSectionRows = (enabled, compliance, runId, paths) => {
  const [rowsBySection, setRowsBySection] = useState({});
  const generation = useRef(0);
  const pathsKey = JSON.stringify(paths);

  useEffect(() => {
    generation.current += 1;
    const current = generation.current;
    setRowsBySection({});
    const queue = JSON.parse(pathsKey);
    if (!enabled || !compliance || !runId || !queue.length) return undefined;
    const work = async () => {
      while (generation.current === current && queue.length) {
        const section = queue.shift();
        const rows = await loadSectionRows(compliance, runId, section);
        if (generation.current !== current) return;
        setRowsBySection((prev) => ({ ...prev, [section]: rows }));
      }
    };
    for (let i = 0; i < SECTION_CONCURRENCY; i += 1) work();
    return () => {
      generation.current += 1;
    };
  }, [enabled, compliance, runId, pathsKey]);

  const retry = useCallback(
    (section) => {
      if (!enabled || rowsBySection[section] !== SECTION_FAILED) return;
      const current = generation.current;
      setRowsBySection((prev) => ({ ...prev, [section]: undefined }));
      loadSectionRows(compliance, runId, section).then((rows) => {
        if (generation.current === current) setRowsBySection((prev) => ({ ...prev, [section]: rows }));
      });
    },
    [enabled, compliance, runId, rowsBySection]
  );

  return { rowsBySection, retry };
};

// "audit" matches the Audit Hub pages: gray shell, white section cards, 13px figures.
const VARIANTS = {
  default: {
    shell: { border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' },
    header: { p: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' },
    title: { fontWeight: 600, color: '#1a1a1a' },
    list: { p: 2, gap: 2 },
    border: '#e0e0e0',
    radius: '8px',
    hover: '#f8f9fa',
    metricSize: 15,
  },
  audit: {
    shell: { ...tableShellSx, p: { xs: 1.5, sm: 2 } },
    header: { mb: 1.5 },
    title: { fontWeight: 700, fontSize: '1rem', color: AH_TEXT },
    list: { gap: 1.25 },
    border: AH_BORDER,
    radius: '12px',
    hover: AH_TRACK,
    metricSize: 13,
  },
};

const ControlsCategoriesView = ({
  controls = [],
  onResourceClick,
  onSectionSelect,
  selectedSection,
  sectionControls,
  isLoadingControls,
  controlsError,
  compliance,
  runId,
  auditRoster,
  variant = 'default',
  embedded = false,
}) => {
  const look = VARIANTS[variant] || VARIANTS.default;
  const isAudit = look === VARIANTS.audit;
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [assigneeEmail, setAssigneeEmail] = useState('');
  const dispatch = useDispatch();
  const {
    isDownloadingComplianceReport,
    downloadComplianceReportMessage,
    downloadComplianceReportError
  } = useSelector((state) => state.compliance);

  // Prepare categories from controls array
  const categories = useMemo(() => {
    if (!Array.isArray(controls)) return [];
    return controls.map((category) => ({
      name: category.Section,
      path: category.SectionPath,
      metrics: {
        total: category.TotalControls,
        passed: category.PassedControls,
        failed: category.FailedControls,
        manual: category.ManualControls,
      },
    }));
  }, [controls]);

  const sectionPaths = useMemo(() => categories.map((category) => category.path).filter(Boolean), [categories]);
  const { rowsBySection, retry: retrySection } = useSectionRows(isAudit, compliance, runId, sectionPaths);
  const assigneeOf = useMemo(() => assigneeLookup(auditRoster?.controls), [auditRoster?.controls]);
  const assigneeOptions = useMemo(() => {
    const seen = new Map();
    (Array.isArray(auditRoster?.controls) ? auditRoster.controls : []).forEach((item) => {
      const email = String(item?.assigneeEmail || '').trim();
      const key = textKey(email);
      if (key && !seen.has(key)) seen.set(key, email);
    });
    if (assigneeEmail && !seen.has(textKey(assigneeEmail))) seen.set(textKey(assigneeEmail), assigneeEmail);
    return [...seen.values()].sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }));
  }, [auditRoster?.controls, assigneeEmail]);

  useEffect(() => {
    setAssigneeEmail('');
  }, [compliance, runId]);

  const matchesAssignee = useCallback(
    (controlId, title) => !assigneeEmail || sameEmail(assigneeOf(controlId, title), assigneeEmail),
    [assigneeEmail, assigneeOf]
  );

  // Passed, Failed and Not Applicable follow the roster, so a review decision updates its section straight away.
  const auditCounts = useMemo(() => {
    if (!isAudit || auditRoster?.status !== 'ready') return {};
    const decisionOf = buildDecisionLookup(auditRoster.controls);
    const counts = {};
    categories.forEach((category) => {
      const rows = rowsBySection[category.path];
      if (!Array.isArray(rows)) return;
      const tally = { passed: 0, failed: 0, notApplicable: 0, total: 0 };
      rows.forEach((row) => {
        if (!matchesAssignee(row.ControlId, row.title)) return;
        tally.total += 1;
        const outcome = controlOutcome(row, decisionOf(row.ControlId, row.title));
        if (outcome) tally[outcome] += 1;
      });
      counts[category.path] = tally;
    });
    return counts;
  }, [isAudit, auditRoster?.status, auditRoster?.controls, categories, rowsBySection, matchesAssignee]);

  const visibleCategories = useMemo(() => {
    if (!isAudit || !assigneeEmail) return categories;
    return categories.filter((category) => {
      const rows = rowsBySection[category.path];
      if (!Array.isArray(rows)) return true;
      return rows.some((row) => matchesAssignee(row.ControlId, row.title));
    });
  }, [isAudit, assigneeEmail, categories, rowsBySection, matchesAssignee]);

  const sectionsStillLoading = Boolean(
    assigneeEmail &&
      categories.some((category) => rowsBySection[category.path] == null)
  );
  const noAssignedControls = Boolean(assigneeEmail) && !sectionsStillLoading && visibleCategories.length === 0;

  const gridControls = useMemo(() => {
    const list = sectionControls?.controlsListJson;
    if (!assigneeEmail || list == null) return list;
    const rows = Array.isArray(list) ? list : Object.values(list);
    return rows.filter((row) => matchesAssignee(row.ControlId, getControlTitle(row, compliance)));
  }, [sectionControls, assigneeEmail, matchesAssignee, compliance]);

  const metricFields = isAudit ? AUDIT_METRICS : DEFAULT_METRICS;
  const metricValue = (category, key) => {
    if (isAudit && assigneeEmail) return auditCounts[category.path]?.[key] ?? '—';
    if (!isAudit || key === 'total') return category.metrics[key];
    return auditCounts[category.path]?.[key] ?? '—';
  };

  const handleCategoryChange = (categoryPath) => (event, isExpanded) => {
    setExpandedCategory(isExpanded ? categoryPath : null);
    if (isExpanded) {
      onSectionSelect(categoryPath);
      retrySection(categoryPath);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={embedded ? { ...look.shell, p: 0, border: 'none', borderRadius: 0, backgroundColor: 'transparent' } : look.shell}
    >
      {/* Header Bar */}
      <Box
        sx={{
          ...look.header,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexWrap: 'wrap',
          columnGap: 2,
          rowGap: 1.25,
        }}
      >
        <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
          <Typography variant="h6" sx={look.title}>
            Compliance Controls
          </Typography>
          {isAudit ? (
            <Typography sx={{ fontSize: 13, color: AH_MUTED, mt: 0.25 }}>
              Expand a section to review its controls.
            </Typography>
          ) : null}
        </Box>
        {isAudit ? (
          <TextField
            select
            size="small"
            value={assigneeEmail}
            aria-label="Assigned To"
            disabled={auditRoster?.status === 'loading'}
            onChange={(event) => setAssigneeEmail(event.target.value)}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => selected || 'Assigned To',
            }}
            sx={{
              ...searchFieldSx,
              width: { xs: '100%', sm: 280 },
              maxWidth: '100%',
              flex: { xs: '1 1 100%', sm: '0 1 280px' },
              '& .MuiSelect-select': {
                color: assigneeEmail ? AH_TEXT : AH_MUTED,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                lineHeight: '40px',
                py: 0,
              },
            }}
          >
            <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All assignees</MenuItem>
            {assigneeOptions.map((email) => (
              <MenuItem key={textKey(email)} value={email} sx={{ fontSize: '0.875rem' }}>
                {email}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
      </Box>

      {/* Accordions Container */}
      <Box sx={{ ...look.list, display: 'flex', flexDirection: 'column' }}>
        {categories.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: AH_MUTED, py: 1 }}>Pending Automated Scan Completion</Typography>
        ) : noAssignedControls ? (
          <Typography sx={{ fontSize: 13, color: AH_MUTED, py: 1 }}>
            No controls are assigned to {assigneeEmail}.
          </Typography>
        ) : null}
        {visibleCategories.map((category) => (
          <Accordion
            key={category.path}
            expanded={expandedCategory === category.path}
            onChange={handleCategoryChange(category.path)}
            sx={{
              '&:before': { display: 'none' },
              boxShadow: 'none',
              border: `1px solid ${look.border}`,
              borderRadius: `${look.radius} !important`,
              overflow: 'hidden',
              '&:hover': {
                boxShadow: '0px 2px 8px rgba(0,0,0,0.05)',
              },
            }}
          >
            <AccordionSummary
              sx={{
                backgroundColor: '#ffffff',
                borderBottom: `1px solid ${look.border}`,
                '&:hover': { backgroundColor: look.hover },
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                  {expandedCategory === category.path ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: isAudit ? '0.9375rem' : '1rem', flex: 1, ...(isAudit ? { color: AH_TEXT } : null) }}>
                  {category.name}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap',
                  }}
                >
                  {metricFields.map((field, index) => (
                    <React.Fragment key={field.key}>
                      {index > 0 ? (
                        <Box
                          component="span"
                          aria-hidden
                          sx={{ width: '1px', height: 14, mx: 0.75, flexShrink: 0, backgroundColor: METRIC_SEPARATOR }}
                        />
                      ) : null}
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{
                          color: field.color,
                          fontWeight: 500,
                          fontSize: look.metricSize,
                          lineHeight: 1.2,
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {field.label}: {metricValue(category, field.key)}
                      </Typography>
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {expandedCategory === category.path ? (
                isLoadingControls ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : controlsError ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography color="error">Error loading controls. Please try again.</Typography>
                  </Box>
                ) : sectionControls && sectionControls.controlsListJson ? (
                  <ControlsGrid
                    controls={gridControls}
                    onResourceClick={onResourceClick}
                    compliance={compliance}
                    auditRoster={auditRoster}
                    auditMode={isAudit}
                  />
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography color="text.secondary">No controls found for this section.</Typography>
                  </Box>
                )
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="text.secondary">Expand to view controls</Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Paper>
  );
};

export default ControlsCategoriesView; 